import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Application } from '../../models';
import { Ability, Loader } from '../../components';
import { updateApplication, acceptApplication, rejectApplication, unsubmitApplication } from '../../redux';
import { Utils } from '../../services';
import { Input, Row, Col, Badge, Button, FormGroup, Label } from 'reactstrap';

import './ApplicationDetails.scss';


const ApplicationDetails = ({ application, isLoading, onApprove, onReject, onUnsubmit, column, readOnly, hideStatus }) => {
    const dispatch    = useDispatch();
    const { t, i18n } = useTranslation();
    const ui_lang = Utils.i18nUiLang(i18n);

    const [max_nb_hours, set_max_nb_hours] = useState(application?.max_nb_hours);
    const [max_nb_hours_per_day, set_max_nb_hours_per_day] = useState(application?.max_nb_hours_per_day);

    const { isLoading: isStatusActionLoading, isLoadingOne } = useSelector(state => state.applications);
    const isActionLoading = isStatusActionLoading || isLoadingOne;

    const save = () => dispatch(updateApplication({
        ...application,
        max_nb_hours,
        max_nb_hours_per_day
    }));
    const accept = () => dispatch(acceptApplication(application.id, onApprove));
    const reject = () => dispatch(rejectApplication(application.id, onReject));
    const unsubmit = () => dispatch(unsubmitApplication(application.id, onUnsubmit));
    const hasAssignedShifts = (application?.nb_hours ?? 0) > 0;

    if (!application)
        return null;

    return (
        <>
            <Row>
                <Col xs={12} xl={column ? 12 : 6}>
                    {isLoading ? <Loader /> : (
                        <>
                            {!hideStatus && (
                                <Row>
                                    <Col xs={12}>
                                        {application.status === Application.STATUS_SUBMITTED && (
                                            <div className="d-flex column gap-2">
                                                <Badge className="px-4" color="info">
                                                    <h3>{ t('applications.status_submitted') }</h3>
                                                </Badge>
                                                <Ability can="applications:editOther">
                                                    <div className="w-100">
                                                        <Button block color="success" onClick={accept} disabled={isActionLoading}>
                                                            <i className={`fa fa-${isActionLoading ? 'spinner fa-spin' : 'check'}`}></i>&nbsp;
                                                            { t('applications.accept_this_application') }
                                                        </Button>
                                                    </div>
                                                    <div className="w-100">
                                                        <Button block color="danger" onClick={reject} disabled={isActionLoading}>
                                                            <i className={`fa fa-${isActionLoading ? 'spinner fa-spin' : 'times'}`}></i>&nbsp;
                                                            { t('applications.reject_this_application') }
                                                        </Button>
                                                    </div>
                                                </Ability>
                                            </div>
                                        )}
                                        {application.status === Application.STATUS_ACCEPTED && (
                                            <div className="d-flex column gap-2">
                                                <Badge className="px-4" color="success">
                                                    <h3>{ t('applications.status_accepted') }</h3>
                                                </Badge>
                                                <Ability can="applications:editOther">
                                                    <div className="w-100">
                                                        <Button
                                                            block
                                                            color="warning"
                                                            onClick={unsubmit}
                                                            disabled={hasAssignedShifts || isActionLoading}
                                                            title={hasAssignedShifts ? t('applications.cannot_unsubmit_with_shifts') : undefined}
                                                        >
                                                            <i className={`fa fa-${isActionLoading ? 'spinner fa-spin' : 'undo'}`}></i>&nbsp;
                                                            { t('applications.unsubmit_this_application') }
                                                        </Button>
                                                    </div>
                                                    <div className="w-100">
                                                        <Button block color="danger" onClick={reject} disabled={isActionLoading}>
                                                            <i className={`fa fa-${isActionLoading ? 'spinner fa-spin' : 'times'}`}></i>&nbsp;
                                                            { t('applications.reject_this_application') }
                                                        </Button>
                                                    </div>
                                                </Ability>
                                            </div>
                                        )}
                                        {application.status === Application.STATUS_REJECTED && (
                                            <div className="d-flex column gap-2">
                                                <Badge className="px-4" color="danger">
                                                    <h3>{ t('applications.status_rejected') }</h3>
                                                </Badge>
                                                <Ability can="applications:editOther">
                                                    <div className="w-100">
                                                        <Button
                                                            block
                                                            color="warning"
                                                            onClick={unsubmit}
                                                            disabled={hasAssignedShifts || isActionLoading}
                                                            title={hasAssignedShifts ? t('applications.cannot_unsubmit_with_shifts') : undefined}
                                                        >
                                                            <i className={`fa fa-${isActionLoading ? 'spinner fa-spin' : 'undo'}`}></i>&nbsp;
                                                            { t('applications.unsubmit_this_application') }
                                                        </Button>
                                                    </div>
                                                    <div className="w-100">
                                                        <Button block color="success" onClick={accept} disabled={isActionLoading}>
                                                            <i className={`fa fa-${isActionLoading ? 'spinner fa-spin' : 'check'}`}></i>&nbsp;
                                                            { t('applications.accept_this_application') }
                                                        </Button>
                                                    </div>
                                                </Ability>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            )}
                            <Row>
                                <Col sm={12}>
                                    <div className="d-flex mb-3">
                                        <Badge color={application.busy_rate >= 100 ? 'danger' : (application.busy_rate >= 75 ? 'warning' : (application.busy_rate > 0 ? 'success' : 'info'))}>
                                            <h5>
                                                { t('applications.busy_rate') } : { application.busy_rate.toFixed(2) } %
                                            </h5>
                                        </Badge>
                                    </div>
                                    {readOnly ? (
                                        <table className="table">
                                            <tr>
                                                <td align="left">{t('applications.max_nb_hours')}</td>
                                                <th className="text-right">{max_nb_hours}</th>
                                            </tr>
                                            <tr>
                                                <td align="left">{t('applications.max_nb_hours_per_day')}</td>
                                                <th className="text-right">{max_nb_hours_per_day}</th>
                                            </tr>
                                        </table>
                                    ) : (
                                        <>
                                            <FormGroup row>
                                                <Label for="max_nb_hours" sm={6}>{t('applications.max_nb_hours')}</Label>
                                                <Col sm={6}>
                                                    <Input className="form-control" type="number" value={ max_nb_hours } onChange={ e => set_max_nb_hours(e.target.value) }/>
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row>
                                                <Label for="max_nb_hours_per_day" sm={6}>{t('applications.max_nb_hours_per_day')}</Label>
                                                <Col sm={6}>
                                                    <Input className="form-control" type="number" value={ max_nb_hours_per_day } onChange={ e => set_max_nb_hours_per_day(e.target.value) }/>
                                                </Col>
                                            </FormGroup>
                                        </>
                                    )}
                                </Col>
                            </Row>
                            {!readOnly && (
                                <Row className="main">
                                    <Col className="text-center">
                                        <Button type="submit" color="success" onClick={ save }>
                                            <span>
                                                <i className={`fa ${isLoadingOne ? 'fa-spinner fa-spin' : 'fa fa-save'}`}></i>&nbsp;{t("common.save")}
                                            </span>
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}
                </Col>
                <Col xs={12} xl={column ? 12 : 6}>
                    <Row>
                        <Col sm={12}>
                            <dl className="ApplicationDetails-answers">
                            {application.values?.map(value => {
                                const label = Utils.localized_or_fallback(
                                    value.field?.label,
                                    ui_lang,
                                    value.field?.name || ''
                                );
                                return (
                                    <div key={value.id} className="ApplicationDetails-answer">
                                        <dt>{label}</dt>
                                        <dd>{value.renderData({ t, lang: ui_lang })}</dd>
                                    </div>
                                );
                            })}
                            </dl>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </>
    );
}

export default ApplicationDetails;
